import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CustomButton from "../button/button";
import OutlineCustomButton from "../button/outlineButton";
import { ThemeColor } from "../theme/color";
import { FontFamily } from "../theme/fontFamily";
import { fontWeight } from "../theme/fontWeight";
import {
  PayoutModalHeader,
  payoutModalBodySx,
  payoutModalFooterSx,
  payoutModalStyle,
} from "./payoutModal";

/**
 * Asks which organisation and intake to report on.
 *
 * The other exports work off ticked rows, which cannot express "everything you
 * have for this organisation this intake" - and that is exactly what an
 * organisation is sent, including the students whose commission was refused.
 */
export const OrganisationExportModal = ({
  open,
  onClose,
  onExport,
  organisations = [],
  universities = [],
  intakes = [],
  loading = false,
  // The invoice export is already limited to what can be invoiced, so the
  // approved-only tick only makes sense on the student report.
  showApprovedOnly = false,
  // The student report is read one semester at a time; leaving the intake
  // blank there reported every semester at once.
  requireIntake = false,
  title = "Export For Organisation",
  description = "Every student this organisation has for the chosen intake and university is included - approved, not approved and pending alike.",
}) => {
  const [organizationId, setOrganizationId] = useState("");
  const [university, setUniversity] = useState("");
  const [intake, setIntake] = useState("");
  const [approvedOnly, setApprovedOnly] = useState(false);

  const close = () => {
    setOrganizationId("");
    setUniversity("");
    setIntake("");
    setApprovedOnly(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={close}>
      <Box sx={payoutModalStyle(560)}>
        <PayoutModalHeader title={title} onClose={close} />

        <Box sx={payoutModalBodySx}>
          <Typography
            sx={{ mb: 2.5, color: ThemeColor.GREY, fontSize: 13 }}
            fontFamily={FontFamily.Lato}
          >
            {description}
          </Typography>

          {/* Both lists grow with the account, so they are typed into rather
              than scrolled through. */}
          <Autocomplete
            options={organisations}
            getOptionLabel={(org) => org.title || org.organizationName || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={
              organisations.find((org) => org._id === organizationId) || null
            }
            onChange={(event, picked) =>
              setOrganizationId(picked ? picked._id : "")
            }
            renderInput={(params) => (
              <TextField {...params} label="Organisation *" />
            )}
            sx={{ mb: 2.5 }}
          />

          <Autocomplete
            options={universities}
            getOptionLabel={(uni) => uni.title || uni.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={universities.find((uni) => uni._id === university) || null}
            onChange={(event, picked) => setUniversity(picked ? picked._id : "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="University"
                helperText="Leave blank to include every university"
              />
            )}
            sx={{ mb: 2.5 }}
          />

          <Autocomplete
            // The server matches intakes loosely, so an intake outside this
            // generated list can simply be typed in.
            freeSolo
            options={intakes}
            // The typed text IS the answer here, so `inputValue` is the single
            // source of truth. Driving `value` as well made the two fight and
            // the list stopped narrowing as you typed.
            inputValue={intake}
            onInputChange={(event, typed) => setIntake(typed || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={requireIntake ? "Intake *" : "Intake"}
                helperText={
                  requireIntake
                    ? "Sets the semester being reported on"
                    : "Leave blank to include every intake"
                }
              />
            )}
          />

          {showApprovedOnly ? (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={approvedOnly}
                    onChange={(event) => setApprovedOnly(event.target.checked)}
                    sx={{ color: ThemeColor.PURPLE, "&.Mui-checked": { color: ThemeColor.PURPLE } }}
                  />
                }
                label={
                  <Typography
                    fontFamily={FontFamily.Lato}
                    sx={{ fontSize: 14, fontWeight: fontWeight.W500 }}
                  >
                    Only commission approved students
                  </Typography>
                }
              />
              <Typography
                fontFamily={FontFamily.Lato}
                sx={{ fontSize: 12, color: ThemeColor.GREY, ml: 4, mt: -0.5 }}
              >
                Tick this for a settlement list - pending, deferred and refused
                students are left out.
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box sx={payoutModalFooterSx}>
          <OutlineCustomButton variant="outlined" onClick={close}>
            Cancel
          </OutlineCustomButton>
          <CustomButton
            variant="contained"
            backgroundColor={ThemeColor.PURPLE}
            color={ThemeColor.WHITE}
            loading={loading}
            disabled={!organizationId || (requireIntake && !intake.trim())}
            onClick={() =>
              onExport({ organizationId, university, intake, approvedOnly })
            }
          >
            Export
          </CustomButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default OrganisationExportModal;
